<template>
  <div>
    <timesheet-created-not-saved-warning :value="!Boolean(timesheetId) && !Boolean(batch)"/>
    <v-card flat>
      <v-card-text class="pa-0 pt-2">
        <confirm-dialog
          :is-open="isSaveDialogOpen"
          :dialog-header="$t('timesheetSaveHeader')"
          :dialog-body="$t('timesheetSaveBody')"
          :is-loading="isLoading"
          :is-disabled="!!queue"
          :confirm-button-text="!!queue ? `${$t('generating')} ${queue.progress }%` : $t('save')"
          @confirm="saveTimesheet"
          @dialogToggle="val => isSaveDialogOpen = val"
        />

        <employee-timesheet-editor-table
          :is-creating="isCreating"
          :timesheet="timesheet"
          :is-loading="isLoading"
          :schema="schema"
          :month="month"
        />
      </v-card-text>
      <v-card-actions v-if="timesheet.length && !batch">
        <v-spacer/>
        <v-btn
          :loading="isLoading"
          :disabled="isLoading"
          color="primary"
          @click.native="isSaveDialogOpen = true">{{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import TimesheetApi from '@/api/TimesheetApi'
import CompanyApi from '@/api/CompanyApi'

import { mapGetters } from 'vuex'
import ConfirmDialog from '@/components/global-components/confirmDialog/ConfirmDialog'
import EmployeeTimesheetEditorTable from '@/components/routes/employees/timesheets/EmployeeTimesheetEditorTable'
import TimesheetCreatedNotSavedWarning from '@/components/routes/employees/timesheets/TimesheetCreatedNotSavedWarning'

export default {
  name: 'EmployeeTimesheetEditor',
  components: { TimesheetCreatedNotSavedWarning, EmployeeTimesheetEditorTable, ConfirmDialog },
  props: ['timesheetId', 'batch'],
  data () {
    return {
      isLoading: false,
      isCreating: false,
      isSaveDialogOpen: false,
      timesheet: [],
      month: null,
      schemaId: null,
      lastDayOfCalculation: null,
      queueInterval: null,
      queue: null
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee']),
    schema () {
      return this.batch ? this.batch.schema : this.currentEmployee.schema
    }
  },
  methods: {
    async getTimesheet () {
      const successResponse = await this.getTimesheetByRole()

      if (!successResponse) return

      this.timesheet = successResponse.days
      this.schemaId = successResponse.schema_id
      this.month = successResponse.month
    },

    async createTimesheet () {
      const successResponse = await this.createTimesheetByRole()

      if (!successResponse) return

      this.timesheet = successResponse
    },

    async saveTimesheet () {
      this.isLoading = true
      try {
        if (this.isCreating) {
          this.queue = await await this.saveTimesheetByRole()
        } else {
          this.queue = await await this.editTimesheetByRole()
        }
      } catch (e) {
        this.$notification(e)
        this.isLoading = false
      }
      this.isLoading = false

      this.queueInterval = setInterval(async () => {
        try {
          this.queue = await CompanyApi.getQueue(this.queue.id)
          if (this.queue.is_finished || this.queue.progress > 100) {
            this.queue = null
            clearInterval(this.queueInterval)
            this.isSaveDialogOpen = false
            this.navigateByRole()
          }
        } catch (e) {
          this.$notification(e)
          this.queue = null
          clearInterval(this.queueInterval)
        }
      }, 1000)
    },

    getTimesheetByRole () {
      return this.timesheetActionDecorator('read', 'get', this.timesheetId)
    },

    createTimesheetByRole () {
      return this.timesheetActionDecorator('create', 'create', this.schemaId, this.month, this.lastDayOfCalculation)
    },
    saveTimesheetByRole () {
      return this.timesheetActionDecorator('create', 'save', this.schemaId, this.month, this.timesheet)
    },

    editTimesheetByRole () {
      return this.timesheetActionDecorator('edit', 'edit', this.timesheetId, this.schemaId, this.month, this.timesheet)
    },

    async timesheetActionDecorator (rule, action, ...rest) {
      let response
      this.isLoading = true
      try {
        response = await TimesheetApi[`${action}Timesheet`](...rest)
      } catch (e) {
        this.$notification(e)

        this.$router.push({ name: 'employee', params: { employeeId: this.$route.params.employeeId } })
      }
      this.isLoading = false
      return response
    },

    navigateByRole () {
      this.$router.push({ name: 'employee', params: { employeeId: this.currentEmployee.id } })
    }
  },

  watch: {
    dialog (val) {
      val || this.close()
    }
  },

  created () {
    const query = this.$route.query

    if (this.timesheetId) {
      this.getTimesheet()
    } else if (this.batch && query.month) {
      this.timesheet = this.batch.days
      this.schemaId = this.batch.schema.id
      this.month = query.month
    } else if (query.lastDayOfCalculation && query.month && query.schemaId) {
      this.lastDayOfCalculation = query.lastDayOfCalculation
      this.schemaId = query.schemaId
      this.month = query.month
      this.isCreating = true
      this.createTimesheet()
    }
  },
  i18n: {
    messages: {
      de: {
        save: 'Speichern',
        timesheetSaveHeader: 'Bestätigung',
        timesheetSaveBody: 'Stimmen die vorliegenden Daten mit der Realität überein?',
        generating: 'Erstellen...'
      },
      en: {
        save: 'Save',
        timesheetSaveHeader: 'Confirm',
        timesheetSaveBody: 'Do the available data correspond to reality?',
        generating: 'Generating...'
      },
      tr: {
        save: 'Kaydet',
        timesheetSaveHeader: 'Onayla',
        timesheetSaveBody: 'Verinin doğruluğunu onaylıyor musunuz?',
        generating: 'Yaratma...'
      },
      ru: {
        save: 'Сохранить',
        timesheetSaveHeader: 'Подтвердить',
        timesheetSaveBody: 'Соответствуют ли имеющиеся данные действительности?',
        generating: 'Генерация...'
      }
    }
  }
}
</script>
